import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Mic, ClipboardList, Users, User, LogOut, Shield, Package, Library } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SocialLinks from "@/components/SocialLinks";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useI18n();
  const isAdmin = user?.role === "platform_admin";
  const isContentManager = user?.role === "content_manager";
  const canManageContent = isAdmin || isContentManager;

  const tabs = [
    { to: "/home", label: t("nav.home"), icon: Home, testid: "nav-home" },
    { to: "/assess", label: t("nav.assess"), icon: Mic, testid: "nav-assess" },
    { to: "/voicelab", label: t("nav.lab"), icon: ClipboardList, testid: "nav-voicelab" },
    { to: "/experts", label: t("nav.experts"), icon: Users, testid: "nav-experts" },
    { to: "/therapies", label: t("nav.care"), icon: Package, testid: "nav-therapies" },
    { to: "/me", label: t("nav.me"), icon: User, testid: "nav-me" },
  ];

  return (
    <div className="App min-h-screen bg-[color:var(--mano-bg)]">
      {/* Desktop sidebar */}
      <aside
        data-testid="side-nav"
        className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 border-r border-[color:var(--mano-border)] bg-white flex-col p-6"
      >
        <Link to="/home" className="flex flex-col items-start gap-2 mb-10" data-testid="brand-link">
          <div className="font-display font-bold text-[color:var(--mano-primary)] leading-none tracking-tight text-4xl">
            ManoV<span className="text-[color:var(--mano-ink)]">āk</span>
            <sup className="text-[10px] font-semibold text-[color:var(--mano-ink-soft)] ml-0.5 align-top">™</sup>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[color:var(--mano-ink-soft)]">
              by
            </span>
            <img
              src="/manodayam-wordmark.png"
              alt="Manodayam"
              className="h-4 w-auto object-contain opacity-80"
            />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[color:var(--mano-primary)]">
            Voice · Mind · Care
          </div>
        </Link>
        <nav className="flex flex-col gap-1">
          {tabs.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid + "-desktop"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-[color:var(--mano-primary)] text-white"
                    : "hover:bg-[color:var(--mano-border)]/50 text-[color:var(--mano-ink)]"
                }`
              }
            >
              <Icon size={18} /> <span>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin-desktop"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl mt-4 border border-[color:var(--mano-border)] ${
                  isActive ? "bg-[color:var(--mano-ink)] text-white" : "text-[color:var(--mano-ink)]"
                }`
              }
            >
              <Shield size={18} /> Admin
            </NavLink>
          )}
          {canManageContent && (
            <NavLink
              to="/admin/datp"
              data-testid="nav-datp-desktop"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl mt-2 border border-[color:var(--mano-border)] ${
                  isActive ? "bg-[color:var(--mano-primary)] text-white" : "text-[color:var(--mano-ink)]"
                }`
              }
            >
              <Library size={18} /> Therapy content
            </NavLink>
          )}
        </nav>
        <div className="mt-auto">
          <div className="mb-3"><LanguageSwitcher testid="lang-switcher-desktop" /></div>
          <div className="text-sm text-[color:var(--mano-ink-soft)] mb-2">
            Signed in as <span className="font-semibold">{user?.name}</span>
          </div>
          <button
            data-testid="logout-btn-desktop"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--mano-border)] hover:bg-[color:var(--mano-border)]/40"
          >
            <LogOut size={16} /> Log out
          </button>
          <div className="mt-5 pt-4 border-t border-[color:var(--mano-border)]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-ink-soft)] mb-2">
              Follow us
            </div>
            <SocialLinks testid="sidebar-social-links" size={16} />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[color:var(--mano-border)] px-5 py-3 flex items-center justify-between">
        <Link to="/home" className="flex flex-col items-start gap-0.5" data-testid="brand-link-mobile">
          <div className="font-display font-bold text-[color:var(--mano-primary)] leading-none tracking-tight text-2xl">
            ManoV<span className="text-[color:var(--mano-ink)]">āk</span>
            <sup className="text-[8px] font-semibold text-[color:var(--mano-ink-soft)] ml-0.5 align-top">™</sup>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[8px] font-semibold uppercase tracking-[0.22em] text-[color:var(--mano-ink-soft)]">
              by
            </span>
            <img
              src="/manodayam-wordmark.png"
              alt="Manodayam"
              className="h-3 w-auto object-contain opacity-80"
            />
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact testid="lang-switcher-mobile" />
          {isAdmin && (
            <Link
              to="/admin"
              data-testid="nav-admin-mobile"
              className="text-[color:var(--mano-primary)] p-2"
              aria-label="Admin"
            >
              <Shield size={18} />
            </Link>
          )}
          {canManageContent && (
            <Link
              to="/admin/datp"
              data-testid="nav-datp-mobile"
              className="text-[color:var(--mano-primary)] p-2"
              aria-label="Therapy content"
            >
              <Library size={18} />
            </Link>
          )}
          <button
            data-testid="logout-btn-mobile"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="text-[color:var(--mano-ink-soft)] p-2"
            aria-label="Log out"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen pb-safe lg:pb-8">
        <div className="max-w-5xl mx-auto px-5 py-6 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        data-testid="bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[color:var(--mano-border)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-6">
          {tabs.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid + "-mobile"}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium ${
                  isActive ? "text-[color:var(--mano-primary)]" : "text-[color:var(--mano-ink-soft)]"
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin-mobile"
              className={({ isActive }) =>
                `hidden ${""}`
              }
            />
          )}
        </div>
      </nav>
    </div>
  );
}
